import React, {useMemo} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps, useEffectEvent, useEffectItem} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import assistant from 'helpers/assistant';
import StyledDatabaseEntitiesTableBar
  from 'components/organisms/Bars/DatabaseEntitiesTableBar/DatabaseEntitiesTableBar.styles';
import {useSplitPath} from 'helpers/hooks/links';
import {useTable} from 'components/organisms/Providers/TableProvider/TableProvider';
import useMediaQuery from '@mui/material/useMediaQuery';
import {useCollectionAdd} from 'services/collection/collection.hooks';
import {useCollectionPatch, useCollectionSelected} from 'services/collection/collection.utils';
import searchUtils from 'helpers/search';
import SaveSearchDialog from 'components/organisms/Dialogs/SaveSearchDialog/SaveSearchDialog';
import Save from '@mui/icons-material/Save';
import constants from 'helpers/constants';
import {useSnackbar} from 'components/organisms/Providers/SnackbarProvider/SnackbarProvider';
import {useAuthUser} from 'components/organisms/Providers/AuthProvider/AuthProvider';

const DatabaseEntitiesTableBar = React.forwardRef((props, ref) => {
  const innerProps = useComponentProps(props, 'DatabaseEntitiesTableBar');

  const user = useAuthUser();
  const tableProvider = useTable();
  const isLoading = tableProvider.isLoading();

  const listState = useEffectItem(tableProvider.appliedListState());

  const [, pathPostfix] = useSplitPath('database');

  const smDown = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const mdDown = useMediaQuery((theme) => theme.breakpoints.down('md'));

  const snackbar = useSnackbar();

  const addCollection = useCollectionAdd();
  const patchCollection = useCollectionPatch();
  const selectCollections = useCollectionSelected();

  const entityAssignEvent = useEffectEvent(tableProvider.updaters?.assignItems);
  const createViewEvent = useEffectEvent(tableProvider.updaters?.createView);
  const updateViewEvent = useEffectEvent(tableProvider.updaters?.updateView);

  const saveAction = useMemo(() => {
    const disabled = isLoading || !(searchUtils.filterCount(listState.query) > 0);

    return {
      auth: utils.createAuth({attribute: 'database.entity.save'}),
      label: !mdDown ? 'Save search' : null,
      tooltip: mdDown ? 'Save search' : null,
      icon: Save,
      ActionButtonProps: {
        color: 'primary'
      },
      ActionIconButtonProps: {
        color: 'primary'
      },
      ButtonProps: {
        disabled: disabled
      },
      IconButtonProps: {
        disabled: disabled
      }
    }
  }, [isLoading, listState.query, mdDown]);

  const assistantAction = useMemo(() => ({
    auth: utils.createAuth({ attribute: 'database.entity.assistant' }),
  }), []);

  const columnAction = useMemo(() => ({
    auth: utils.createAuth({ attribute: 'database.entity.columns' }),
  }), []);

  const handleSaveSearchSubmit = (search) => {
    const addSource = (collection) => {
      const assistantType = tableProvider?.assistantState?.assistantType;
      const assistantConversation = tableProvider?.assistantState?.assistantConversation;
      const processedMessage = assistant.processedMessage(assistantType, assistantConversation);
      const type = (listState.query.assistant && processedMessage) ? constants.data.lookup('sourceTypes', constants.sources.types.analyser) :
        constants.data.lookup('sourceTypes', constants.sources.types.database);
      const entityIds = (listState.query.assistant && processedMessage) ? processedMessage.results.entities?.map((e) => +e.entityId) : null;
      const filter = (listState.query.assistant && processedMessage) ? null : searchUtils.advancedQuery(listState.query);

      const data = {type: type.value, name: search.sourceName, filter, entityIds, sourceId: -1};

      const sources = (collection?.projectSources ?? []).concat(data);
      sources[sources.length - 1].sourceId = (collection?.projectSources ?? []).reduce((m, s) => +s.sourceId < m ? +s.sourceId : m, 0) - 1;

      const sourcesValue = utils.uniqueArray(sources, 'sourceId', true);

      return patchCollection(collection, {projectSources: sourcesValue});
    }

    const addEntities = (collection) => {
      const resultsCount = Math.min(tableProvider.listSelection.max, tableProvider.list?.meta?.resultsCount);

      const message = `Adding the compan${resultsCount === 1 ? 'y' : 'ies'} to the collection`;
      return utils.observePromise(utils.wait(() => {
        const selected = search.selected();
        if (selected.failed) {
          return Promise.reject();
        } else {
          return utils.isDefined(selected.entities);
        }
      }, constants.delay.selection).then(() => {
        const selected = search.selected();
        return entityAssignEvent?.({
          collectionId: collection.collectionId,
          entityIds: selected.entities?.map((e) => +e.entityId)
        });
      }), () => {
        return snackbar.show(`${message}...`, null, {
          color: 'info',
          autoHideDuration: null
        });
      }, () => {
        return snackbar.show(`The compan${resultsCount === 1 ? 'y' : 'ies'} ${resultsCount === 1 ? 'was' : 'were'} added to the collection`, null,
          {color: 'success', autoHideDuration: constants.delay.success});
      }, () => {
        return snackbar.show(`${message} failed`, null,
          {color: 'error', autoHideDuration: constants.delay.error});
      }, (id) => {
        snackbar.hide(id);
      });
    }

    if ([constants.query.saveTypes.collection, constants.query.saveTypes.source].includes(search.type.value)) {
      if (search.collectionType === constants.collection.createTypes.create) {
        return addCollection.mutation.mutateAsync({
          name: search.collectionName,
          visibility: search.collectionVisibility
        })
          .then(({response}) => {
            const newCollection = utils.camelcase(response.data?.data);

            if (search.type.value === constants.query.saveTypes.source) {
              return addSource(newCollection);
            } else {
              return addEntities(newCollection);
            }
          });
      } else {
        if (search.type.value === constants.query.saveTypes.source) {
          // the picker's card carries no sources, so read the whole collection before adding one
          return utils.asPromiseCallback(selectCollections)({}, 0, 1, [search.collection.collection.collectionId], false, false)
            .then((collections) => addSource(collections[0]));
        } else {
          return addEntities(search.collection.collection);
        }
      }
    } else if ([constants.query.saveTypes.view].includes(search.type.value)) {
      if (Boolean(tableProvider.view?.userView)) {
        return updateViewEvent?.(tableProvider.view?.userView, {settings: tableProvider.viewSettings()});
      } else {
        const view = {
          name: search.viewName,
          relationId: 0,
          relationType: constants.view.relationTypes.database
        };
        view.subscriptions = [{
          userId: +user?.userId
        }];
        view.settings = tableProvider.viewSettings();

        return createViewEvent?.(view)
          .then((res) => {
            const view = utils.camelcase(res.response.data.data);
            tableProvider.listState.setView({view: view.viewId, appliedAt: (new Date(view.updatedAt)).toISOString()});
          });
      }
    }
  }

  const handleSaveSearchClose = () => {
    tableProvider.closeSave?.();
  }

  const entitiesCount = utils.formatNumber(tableProvider.list?.meta?.resultsCount ?? 0);

  innerProps.title = innerProps.title ?? `${entitiesCount} compan${(tableProvider.list?.meta?.resultsCount ?? 0) === 1 ? 'y' : 'ies'}`;

  innerProps.className = utils.flattenClassName(innerProps.className);

  return <StyledDatabaseEntitiesTableBar ref={ref} {...innerProps}
                                         columnAction={columnAction}
                                         assistantAction={assistantAction}
                                         saveAction={saveAction}
                                         hideColumn={Boolean(pathPostfix) || smDown}
                                         hideSearch={true}
                                         hideAssistant={true}
                                         hideFilter={true}
                                         hideSave={false}>
    {tableProvider.state?.showSave ? <SaveSearchDialog open={true}
                                                       tableProvider={tableProvider}
                                                       onClose={handleSaveSearchClose}
                                                       onSubmit={handleSaveSearchSubmit}/> : null}
  </StyledDatabaseEntitiesTableBar>
});

DatabaseEntitiesTableBar.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ])
};

export default DatabaseEntitiesTableBar;
